import * as angular from 'angular';
import ngRedux from 'ng-redux';
import * as d3 from 'd3';
import * as _ from 'lodash';
import * as CodeMirror from 'codemirror';

import 'codemirror/addon/selection/active-line';
import 'codemirror/addon/hint/show-hint';
import 'codemirror/mode/python/python';

import {
    ComponentDefinition,
    Group,
    ReconciliationResults,
    ScriptTestMode,
    ScriptType
} from '../../models';
import { IGroupService, IDateRangeService, IFileManagementService, ILoggerService } from '../../services';
import { v2scriptKeywords, v3scriptKeywords } from '../../common';
import * as events from '../../common/events';
import { GROUP_UPDATE_SUCCESS } from '../../stateManagement/constants.index';
import { IGroupActionsService, IValueViewModelActionsService } from '../../stateManagement';

interface ICodeEditor {
    ctrl: IScriptControl;
    isReadOnly: boolean;
}

export interface IScriptControl {
    cancel: () => void;
    reconcile: (begin: boolean) => void;
    redo: () => void;
    refresh: () => void;
    saveAndCalculateOccurence: (begin: boolean) => void;
    saveFile: (name) => void;
    test: () => void;
    testMatrix: boolean;
    undo: () => void;
}

export class CodeEditorController implements ICodeEditor, angular.IComponentController {
    static controllerId = 'CodeEditorController';

    codeMirrorOptions = {
        'autoCloseBrackets': true,
        'autoRefresh': true,
        'extraKeys': { 'Ctrl-Space': 'autocomplete' },
        'firstLineNumber': 1,
        'lineNumbers': true,
        'lineWrapping': true,
        'matchBrackets': true,
        'mode': 'python',
        'readOnly': false,
        'styleActiveLine': true,
        'theme': 'xq-light',
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
        highlightSelectionMatches: {
            minChar: 2
        }
    };

    codemirrorLoaded: (cmEditor: CodeMirror.Editor) => void;
    ctrl: IScriptControl;
    editor: CodeMirror.Editor;
    formCtrl: angular.IFormController;
    group: Group;
    isCodeEditorDisabled: boolean;
    isLoading = false;
    isReadOnly: boolean;
    loadingMessage = '';
    reconciliationResults: ReconciliationResults;
    scriptTestMode: ScriptTestMode;

    private _originalCode: string;
    private _timeout: angular.IPromise<void>;
    private _unsubscribe: () => void;
    private groupUpdated: (group: Group) => void;
    private loadGroups: () => void;

    static $inject = [
        '$scope',
        '$rootScope',
        '$timeout',
        '$translate',
        '$ngRedux',
        'groupService',
        'dateRangeService',
        'fileManagementService',
        'loggerService',
        'groupActionsService',
        'valueViewModelActionsService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$timeout: angular.ITimeoutService,
        private _$translate: angular.translate.ITranslateService,
        private _$ngRedux: ngRedux.INgRedux,
        private _groupService: IGroupService,
        private _dateRangeService: IDateRangeService,
        private _fileManagementService: IFileManagementService,
        private _loggerService: ILoggerService,
        private _gas: IGroupActionsService,
        private _vmas: IValueViewModelActionsService
    ) {
    }

    $onInit() {
        this.setCodeMirrorOptions(this.group.scriptType);
        const self = this;

        this.ctrl = {
            test: () => self.test(),
            saveAndCalculateOccurence: (begin: boolean) => self.save(true, begin),
            cancel: () => self.cancel(),
            undo: () => self.undo(),
            redo: () => self.redo(),
            refresh: () => this.editor.refresh(),
            reconcile: (begin: boolean) => self.reconcile(begin),
            saveFile: (name) => self.saveFile(name),
            testMatrix: false
        };
        self.codemirrorLoaded = self.cmLoaded(); // initialize CodeMirror

        this._$scope.$watch(() => this.group, (newValue) => {
            if (newValue) {
                this.cancelTimeout();
                this._timeout = this._$timeout(() => this.reload());
            }
        });

        this._$scope.$watch(() => this.group.scriptType, (newValue, _oldValue) => {
            this.setCodeMirrorOptions(newValue);
        });

        this._$scope.$watch(() => this.isReadOnly, (newValue) => {
            if (newValue) {
                self.editor.setOption('readOnly', true);
                self.editor.setOption('styleActiveLine', false);
            } else {
                self.editor.setOption('readOnly', false);
                self.editor.setOption('styleActiveLine', true);
            }
        });

        this._unsubscribe = this._$ngRedux.connect(null, dispatch => {
            return {
                groupUpdated: (group: Group) => {
                    dispatch(this._vmas.getAll(true));
                    return dispatch({ type: GROUP_UPDATE_SUCCESS, payload: group });
                },
                loadGroups: () => dispatch(this._gas.getAll(true))
            };
        })(this);
    }

    $onDestroy() {
        this.cancelTimeout();
        this._unsubscribe();
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj && changesObj['isCodeEditorDisabled']) {
            const isCodeEditorDisabled = !!changesObj['isCodeEditorDisabled'].currentValue;
            if (this.editor) {
                this.editor.setOption('readOnly', isCodeEditorDisabled);
                this.setCodeMirrorOptions(this.group.scriptType);
            }
        }
    }

    private setCodeMirrorOptions(scriptType: ScriptType) {
        const commonKeywords = ['as', 'assert', 'break', 'class', 'continue',
            'def', 'del', 'elif', 'else', 'except', 'finally',
            'for', 'from', 'global', 'if', 'import',
            'lambda', 'pass', 'raise', 'return',
            'try', 'while', 'with', 'yield', 'in'];
        const commonBuiltins = ['abs', 'all', 'any', 'bin', 'bool', 'bytearray', 'callable', 'chr',
            'classmethod', 'compile', 'complex', 'delattr', 'dict', 'dir', 'divmod',
            'enumerate', 'eval', 'filter', 'float', 'format', 'frozenset',
            'getattr', 'globals', 'hasattr', 'hash', 'help', 'hex', 'id',
            'input', 'int', 'isinstance', 'issubclass', 'iter', 'len',
            'list', 'locals', 'map', 'max', 'memoryview', 'min', 'next',
            'object', 'oct', 'open', 'ord', 'pow', 'property', 'range',
            'repr', 'reversed', 'round', 'set', 'setattr', 'slice',
            'sorted', 'staticmethod', 'str', 'sum', 'super', 'tuple',
            'type', 'vars', 'zip', '__import__', 'NotImplemented',
            'Ellipsis', '__debug__'];

        const isV3 = scriptType === ScriptType.PythonV3;
        let customKeywords;
        if (isV3) {
            customKeywords = v3scriptKeywords
                .concat(['nonlocal', 'False', 'True', 'None', 'async', 'await'])
                .concat(['ascii', 'bytes', 'exec', 'print']);
        } else {
            customKeywords = v2scriptKeywords
                .concat(['exec', 'print'])
                .concat(['apply', 'basestring', 'buffer', 'cmp', 'coerce', 'execfile',
                    'file', 'intern', 'long', 'raw_input', 'reduce', 'reload',
                    'unichr', 'unicode', 'xrange', 'False', 'True', 'None']);
        }
        const keywords = commonKeywords
            .concat(commonBuiltins)
            .concat(customKeywords)
            .sort((a, b) => a > b ? 1 : -1);

        const pythonOptions = {
            mode: {
                name: 'python',
                version: isV3 ? 3 : 2,
                extra_keywords: _.uniq(customKeywords.map(s => s.split('(')[0]))
            }
        };
        this.codeMirrorOptions = Object.assign({}, this.codeMirrorOptions, pythonOptions);
        if (this.editor) {
            this.editor.setOption('hintOptions', {
                hint: (cm, _options) => {
                    return CodeMirror.hint.fromList(cm, { words: keywords });
                }
            });
        }
    }

    private cancelTimeout() {
        if (this._timeout) {
            this._$timeout.cancel(this._timeout);
            this._timeout = undefined;
        }
    }

    private reload() {
        const doc = this.editor.getDoc();

        this._originalCode = this.group.script;
        if (doc) {
            doc.clearHistory();
            doc.markClean();
        }

        if (this.formCtrl) {
            this.formCtrl.$setPristine();
        }
    }

    private cmLoaded(): (cmEditor: CodeMirror.Editor) => void {
        const self = this;

        return (cmEditor: CodeMirror.Editor) => {
            self.editor = cmEditor;
            const doc = self.editor.getDoc();

            cmEditor.on('change', () => cmEditor.refresh());

            if (doc) {
                doc.clearHistory();
                doc.markClean();
            }
        };
    }

    save(computeOccurence: boolean, beginDate: boolean = false): void {
        this.isLoading = true;
        this.loadingMessage = 'Saving';
        const doc = this.editor.getDoc();

        if (doc && this.group) {
            const guid = this.group.guid;
            const script = doc.getValue();
            const data = {
                script: script,
                libraryCollection: this.group.libraryCollection,
                type: this.group.scriptType
            };

            this._groupService.updateScript(guid, data)
                .then(result => {
                    this.group.isMatrixUpToDate = false;
                    if (computeOccurence) {
                        return this.saveAndComputeOccurence(beginDate);
                    } else {
                        this.groupUpdated(this.group);
                        return result;
                    }
                })
                .then(result => {
                    // if compute occurence return a exitCode
                    // otherwise return a true/false
                    if (result &&
                        ((computeOccurence && result.exitCode === 0) ||
                            (!computeOccurence && result))) {
                        this._loggerService.logSuccess('Script updated.');
                        this._originalCode = this.group.script;
                        doc.clearHistory();
                        this.formCtrl.$setPristine();
                        this.formCtrl.$setUntouched();

                        this.group.isMatrixUpToDate = true;
                        this._$rootScope.$broadcast(events.consolePrint, { message: result.logs });

                    } else {
                        this._loggerService.logError('Occurrence matrix not updated');
                        this._$rootScope.$broadcast(events.consolePrint, { message: result.logs });
                    }
                })
                .catch(reason => {
                    this._loggerService.logError(reason);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    }

    private saveAndComputeOccurence(beginDate: boolean) {
        this.loadingMessage = this._$translate.instant('Updating occurrence matrix');
        return this._groupService
            .updateOccurenceMatrix(this.group.guid,
                this.group.script,
                beginDate
                    ? this._dateRangeService.beginDate() as string
                    : this._dateRangeService.endDate() as string,
                this.group.libraryCollection,
                this.ctrl.testMatrix)
            .then(ret => {
                this._groupService
                    .getByGuid(this.group.guid)
                    .then(updated => {
                        try {
                            angular.copy(updated.group, this.group);
                            this.groupUpdated(updated.group);
                            this._$rootScope.$broadcast(events.groupScriptUpdated);
                        } catch (e) {
                            console.error(e);
                        }
                    });
                return ret;
            });
    }

    cancel(): void {
        this.group.script = this._originalCode;
        this.formCtrl.$setPristine();
        this.formCtrl.$setUntouched();
    }

    undo(): void {
        const doc = this.editor.getDoc();
        if (doc) {
            doc.undo();
        }
    }

    redo(): void {
        const doc = this.editor.getDoc();
        if (doc) {
            doc.redo();
        }
    }

    getLineCount(): number {
        const doc = this.editor.getDoc();
        if (doc) {
            return doc.lineCount();
        } else {
            return 0;
        }

    }

    selectTheme(themeName: string): void {
        this.editor.setOption('theme', themeName);
    }

    test() {
        this.loadingMessage = 'Testing';
        this.isLoading = true;
        let date = new Date().toISOString();
        this._$rootScope.$broadcast(events.consolePrint, { message: `Started execution at ${date}` });

        let selectedDate = this.selectedDate();
        if (!selectedDate) {
            selectedDate = date;
        }
        this._groupService
            .testScript(
                this.group.guid, this.group.script, selectedDate,
                this.scriptTestMode, this.group.libraryCollection
            ).then(data => {
                date = new Date().toISOString();
                this._$rootScope.$broadcast(events.consolePrint,
                    { message: `${data.logs}\nExecution complete at ${date}\n\n` });
                this.isLoading = false;
            }).catch(() => {
                this._$rootScope.$broadcast(events.consolePrint, { message: `Execution failed` });
                this.isLoading = false;
            });
    }

    private selectedDate(): string {
        return this.scriptTestMode === ScriptTestMode.EndDateValue
            ? this._dateRangeService.endDate() as string
            : this._dateRangeService.beginDate() as string;
    }

    reconcile(begin: boolean = false) {
        this.loadingMessage = 'Reconciling...';
        this.isLoading = true;
        this._groupService
            .reconcile(this.group.guid,
                begin
                    ? this._dateRangeService.beginDate() as string
                    : this._dateRangeService.endDate() as string
            ).then(data => {
                this.reconciliationResults = data;
                let message = `${data.status} (${data.returnCode}) - penalty: ${d3.sum(data.results, d => d.penalty)}`;
                message += '\n';
                message += data.constraintResults
                    .map(d => `constraint: ${d.name}, target: ${d.target}, reconciled:${d.reconciledValue}`)
                    .join('\n');
                message += '\n';
                message += data.results
                    .map(d => `tag: ${d
                        .tagName}, average: ${d
                            .averageValue}, reconciled: ${d.reconciledValue}, stdDev: ${d.stdDev}, resolution: ${d.resolution}, penalty: ${d.penalty}`)
                    .join('\n');
                this._$rootScope.$broadcast(events.consolePrint, { message: message });
            }).catch(reason => {
                const msg = reason && reason.message ? reason.message : reason;
                this._$rootScope.$broadcast(events.consolePrint, { message: `Execution failed : ${msg}` });
            }).finally(() => {
                this.isLoading = false;
            });
    }

    saveFile(name) {
        const doc = this.editor.getDoc();
        if (doc) {
            // data encoding is managed by service
            this._fileManagementService
                .write(name, doc.getValue())
                .then(() => {
                    this._loggerService.logSuccess('File updated');
                    this.postSave(doc);
                })
                .catch(reason => {
                    // File is actual saved if syntax error => update everything
                    if (reason && reason.data && reason.data.Message && reason.data.Message.indexOf('SyntaxError>=0')) {
                        this.postSave(doc);
                    }
                    this._loggerService.logError(reason);
                });
        }
    }

    private postSave(doc: CodeMirror.Doc) {
        this._originalCode = this.group.script;
        doc.clearHistory();
        this.formCtrl.$setPristine();
        this.formCtrl.$setUntouched();
        this.loadGroups();
    }
}

export const wtCodeEditorWidget: ComponentDefinition = {
    componentId: 'wtCodeEditor',
    controller: CodeEditorController,
    controllerAs: 'vm',
    template: require('./codeEditor.component.html'),
    bindings: {
        group: '=',
        ctrl: '=',
        isReadOnly: '=',
        formCtrl: '=',
        isLoading: '=?',
        scriptTestMode: '=',
        reconciliationResults: '=',
        isCodeEditorDisabled: '<'
    }
};